import React, { type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__CardInput } from '@admin/schema';

import { LandingLocationCardModal } from './modal';

const LandingLocationCardDeleteButton: FC<{
  card: Landing__Location__CardInput;
  onDelete(): void;
}> = ({ card, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {card.title}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationCardEditButton: FC<{
  card: Landing__Location__CardInput;
  onSave(_: Landing__Location__CardInput): void;
}> = ({ card, onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      {visible && (
        <LandingLocationCardModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          value={card}
        />
      )}
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
    </>
  );
};

const LandingLocationCardNewButton: FC<{
  onSave(_: Landing__Location__CardInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Add
      </Button>
      {visible && (
        <LandingLocationCardModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationCardsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__CardInput>>('cards');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Cards</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Title</th>
              <th className="text-left">Description</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (card, index) =>
                !card._destroy && (
                  <tr key={index}>
                    <td className="text-left">{card.title}</td>
                    <td className="text-left">{card.description}</td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationCardEditButton
                          card={card}
                          onSave={(input) => {
                            const cards = [...field.value];
                            cards[index] = {
                              ...cards[index],
                              ...input,
                            };
                            helpers.setValue(cards);
                          }}
                        />
                        <LandingLocationCardDeleteButton
                          card={card}
                          onDelete={() => {
                            const cards = [...field.value];
                            cards[index] = {
                              ...cards[index],
                              _destroy: !cards[index]._destroy,
                            };
                            helpers.setValue(cards);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationCardNewButton
          onSave={(input) => {
            const cards = [...field.value];
            cards.push(input);
            helpers.setValue(cards);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
